import React, { useState, useMemo, useRef } from 'react';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { updateTask } from '../services/firestore';
import { FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';
import toast from 'react-hot-toast';

const DAY_WIDTH = 40;
const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 60;

const statusColors = {
  planificacion: { bg: '#e5e7eb', bar: '#9ca3af' },
  'en-curso': { bg: '#dbeafe', bar: '#3b82f6' },
  finalizado: { bg: '#dcfce7', bar: '#22c55e' },
};

const priorityBorder = {
  alta: '#ef4444',
  media: '#f59e0b',
  baja: '#6b7280',
};

const GanttView = () => {
  const { user } = useAuth();
  const { filteredTasks: tasks = [], selectedProjectId, selectedFolderId, selectedListId, setSelectedTaskId, setShowTaskDrawer } = useTask();
  const containerRef = useRef(null);

  // View range: start from 2 weeks ago, show 8 weeks
  const [weekOffset, setWeekOffset] = useState(0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const viewStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 14 + weekOffset * 7);
    d.setDay ? d.setDate(d.getDate() - d.getDay() + 1) : d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
  }, [weekOffset]);

  const totalDays = 56; // 8 weeks
  const totalWidth = totalDays * DAY_WIDTH;

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(viewStart);
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [viewStart, totalDays]);

  const weeks = useMemo(() => {
    const wks = [];
    for (let i = 0; i < totalDays; i += 7) {
      const start = days[i];
      wks.push({ start, index: i });
    }
    return wks;
  }, [days, totalDays]);

  const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === 'object' && d.seconds) return new Date(d.seconds * 1000);
    if (typeof d === 'string') return new Date(d);
    return null;
  };

  const getDayIndex = (date) => {
    const diff = Math.floor((date - viewStart) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const isToday = (date) => {
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

  // Tasks with dates for the gantt
  const ganttTasks = useMemo(() => {
    return tasks.map(task => {
      const dueDate = parseDate(task.fechaVencimiento);
      const createdDate = parseDate(task.fechaCreacion);
      // If no due date, use created date as single-day bar
      const start = createdDate || today;
      const end = dueDate || start;
      return { ...task, startDate: start, endDate: end };
    });
  }, [tasks]);

  const [dragging, setDragging] = useState(null);

  const handleBarMouseDown = (e, task) => {
    e.stopPropagation();
    const startX = e.clientX;
    const startDate = new Date(task.endDate);

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const daysDelta = Math.round(dx / DAY_WIDTH);
      const newEnd = new Date(startDate);
      newEnd.setDate(newEnd.getDate() + daysDelta);
      setDragging({ taskId: task.id, newEnd });
    };

    const onUp = async () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (dragging?.newEnd) {
        try {
          await updateTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, dragging.taskId, {
            fechaVencimiento: dragging.newEnd.toISOString()
          });
          toast.success('Fecha actualizada');
        } catch {
          toast.error('Error al actualizar');
        }
      }
      setDragging(null);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const todayIndex = getDayIndex(today);

  const formatMonth = (date) => {
    return date.toLocaleDateString('es-ES', { month: 'short' });
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 overflow-hidden">
      {/* Controls */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 shrink-0">
        <div className="flex items-center gap-2">
          <FiCalendar size={14} className="text-gray-500" />
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
            {viewStart.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setWeekOffset(0)} className="px-2 py-1 text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
            Hoy
          </button>
          <button onClick={() => setWeekOffset(w => w - 1)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500">
            <FiChevronLeft size={14} />
          </button>
          <button onClick={() => setWeekOffset(w => w + 1)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500">
            <FiChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Task Names Panel */}
        <div className="w-52 shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-white dark:bg-gray-900">
          <div className="h-[60px] border-b border-gray-200 dark:border-gray-700 flex items-end px-3 pb-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Tarea</span>
          </div>
          {ganttTasks.map(task => (
            <div
              key={task.id}
              className="h-9 flex items-center px-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer group"
              onClick={() => { setSelectedTaskId(task.id); setShowTaskDrawer(true); }}
            >
              <div className="flex items-center gap-2 min-w-0">
                {task.isMilestone ? (
                  <span className="text-amber-500 text-[10px] shrink-0">◆</span>
                ) : (
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: statusColors[task.estado]?.bar || '#9ca3af' }} />
                )}
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate font-medium">{task.titulo}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-auto" ref={containerRef}>
          <div style={{ width: totalWidth, minHeight: '100%' }} className="relative">
            {/* Header - Weeks */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700" style={{ height: HEADER_HEIGHT }}>
              {/* Month row */}
              <div className="flex h-[30px] border-b border-gray-100 dark:border-gray-800">
                {weeks.map((week, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-center border-r border-gray-100 dark:border-gray-800"
                    style={{ width: 7 * DAY_WIDTH }}
                  >
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {formatMonth(week.start)} {week.start.getDate()}
                    </span>
                  </div>
                ))}
              </div>
              {/* Day row */}
              <div className="flex h-[30px]">
                {days.map((day, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-center border-r border-gray-100 dark:border-gray-800 ${isWeekend(day) ? 'bg-gray-50 dark:bg-gray-800/30' : ''} ${isToday(day) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    style={{ width: DAY_WIDTH }}
                  >
                    <span className={`text-[9px] font-medium ${isToday(day) ? 'text-blue-600 dark:text-blue-400 font-bold' : isWeekend(day) ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {['D', 'L', 'M', 'X', 'J', 'V', 'S'][day.getDay()]} {day.getDate()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Grid Lines */}
            <div className="absolute" style={{ top: HEADER_HEIGHT, left: 0, right: 0, bottom: 0 }}>
              {days.map((day, i) => (
                <div
                  key={i}
                  className={`absolute top-0 bottom-0 border-r ${isWeekend(day) ? 'bg-gray-50/50 dark:bg-gray-800/20 border-gray-100 dark:border-gray-800' : 'border-gray-50 dark:border-gray-800/50'}`}
                  style={{ left: i * DAY_WIDTH, width: DAY_WIDTH }}
                />
              ))}

              {/* Today line */}
              {todayIndex >= 0 && todayIndex < totalDays && (
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                  style={{ left: todayIndex * DAY_WIDTH + DAY_WIDTH / 2 }}
                />
              )}

              {/* Task Bars */}
              {ganttTasks.map((task, rowIndex) => {
                const startIdx = getDayIndex(task.startDate);
                const endDate = dragging?.taskId === task.id ? dragging.newEnd : task.endDate;
                const endIdx = getDayIndex(endDate);
                const barStart = Math.max(0, startIdx);
                const barEnd = Math.min(totalDays - 1, endIdx);

                if (barEnd < 0 || barStart >= totalDays) {
                  return (
                    <div key={task.id} className="absolute flex items-center" style={{ top: rowIndex * ROW_HEIGHT + 6, left: 8, height: ROW_HEIGHT - 12 }}>
                      <span className="text-[9px] text-gray-400 italic">Sin fechas</span>
                    </div>
                  );
                }

                const left = barStart * DAY_WIDTH + 2;
                const width = Math.max((barEnd - barStart + 1) * DAY_WIDTH - 4, 12);
                const colors = statusColors[task.estado] || statusColors.planificacion;

                // Milestone: render as diamond marker
                if (task.isMilestone) {
                  const milestoneLeft = barEnd * DAY_WIDTH + DAY_WIDTH / 2;
                  return (
                    <div
                      key={task.id}
                      className="absolute cursor-pointer hover:scale-125 transition-transform group"
                      style={{
                        top: rowIndex * ROW_HEIGHT + (ROW_HEIGHT - 20) / 2,
                        left: milestoneLeft - 10,
                      }}
                      onClick={() => { setSelectedTaskId(task.id); setShowTaskDrawer(true); }}
                      title={task.titulo}
                    >
                      <div
                        className="w-5 h-5 rotate-45 border-2"
                        style={{
                          backgroundColor: colors.bar,
                          borderColor: priorityBorder[task.prioridad] || '#6b7280',
                          opacity: task.estado === 'finalizado' ? 0.6 : 1,
                        }}
                      />
                    </div>
                  );
                }

                return (
                  <div
                    key={task.id}
                    className="absolute rounded-md cursor-pointer hover:shadow-md transition-shadow group"
                    style={{
                      top: rowIndex * ROW_HEIGHT + 6,
                      left,
                      width,
                      height: ROW_HEIGHT - 12,
                      backgroundColor: colors.bar,
                      borderLeft: `3px solid ${priorityBorder[task.prioridad] || '#6b7280'}`,
                      opacity: task.estado === 'finalizado' ? 0.6 : 1,
                    }}
                    onClick={() => { setSelectedTaskId(task.id); setShowTaskDrawer(true); }}
                  >
                    <div className="px-2 h-full flex items-center overflow-hidden">
                      <span className="text-[10px] text-white font-medium truncate drop-shadow-sm">{task.titulo}</span>
                    </div>
                    {/* Resize handle */}
                    <div
                      className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 bg-white/30 rounded-r"
                      onMouseDown={(e) => handleBarMouseDown(e, task)}
                    />
                  </div>
                );
              })}
            </div>

            {/* Row separators */}
            <div style={{ marginTop: HEADER_HEIGHT }}>
              {ganttTasks.map((_, i) => (
                <div key={i} className="border-b border-gray-100 dark:border-gray-800" style={{ height: ROW_HEIGHT }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttView;
